import { useState, useEffect } from "react";

import { MapPin, Check, Plus, Clock, Sun, MoonStar, Sparkles } from "lucide-react";
import { CAT_CLASS, CAT_LABEL, placeImg } from "../utils/constants";
import { formatDuration } from "../utils/smartDuration";

export const ThemeToggle = () => {
  const [dark, setDark] = useState(() => {
    if (typeof window !== 'undefined') return document.documentElement.classList.contains('dark');
    return false;
  });
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('ju-theme', next ? 'dark' : 'light');
  };
  useEffect(() => {
    // Dark is the default brand experience; light is the opt-out.
    const saved = localStorage.getItem('ju-theme');
    if (saved === 'light') {
      setDark(false); document.documentElement.classList.remove('dark');
    } else {
      setDark(true); document.documentElement.classList.add('dark');
    }
  }, []);
  return (
    <button onClick={toggle} aria-label="Toggle theme"
      className="fixed top-4 right-4 z-50 w-10 h-10 rounded-full flex items-center justify-center shadow-sm hover:scale-110 transition-all"
      style={{background:'var(--zen-s1)',border:'1px solid var(--zen-border)'}}
      data-testid="theme-toggle">
      {dark ? <Sun className="w-4 h-4" style={{color:'var(--cat-foodie-solid)'}} /> : <MoonStar className="w-4 h-4" style={{color:'var(--zen-sub)'}} />}
    </button>
  );
};

export const WikiImg = ({ wiki, alt, mini, image }) => {
  const [src, setSrc] = useState(placeImg(image));
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (image) { setSrc(placeImg(image)); return; }
    if (!wiki) return;
    let cancelled = false;
    fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(wiki)}`)
      .then(r => r.json())
      .then(d => { if (!cancelled && d.thumbnail) setSrc(d.thumbnail.source.replace(/\/\d+px-/, mini ? '/200px-' : '/500px-')); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [wiki, mini, image]);

  const WishlistPlaceholder = ({ small }) => (
    <div className="w-full h-full flex flex-col items-center justify-center px-2 text-center"
      style={{background:'linear-gradient(135deg, var(--zen-s2) 0%, var(--zen-accent) 120%)'}}>
      <Sparkles className={small ? "w-3 h-3" : "w-5 h-5 mb-1"} style={{color:'rgba(255,255,255,0.7)'}} />
      {!small && <span className="text-[8px] mt-1 uppercase tracking-wider font-semibold" style={{color:'rgba(255,255,255,0.5)'}}>from your wishlist</span>}
    </div>
  );

  if (mini) {
    return (
      <div className="shrink-0 overflow-hidden" style={{width:64,height:'100%',minHeight:64,background:'var(--zen-s2)'}}>
        {src ? <img className="w-full h-full object-cover" src={src} alt={alt} loading="lazy" onLoad={() => setLoaded(true)} /> :
          <WishlistPlaceholder small />}
      </div>
    );
  }

  return (
    <div className="zen-card-img-wrap">
      {!loaded && !src && <WishlistPlaceholder />}
      {!loaded && src && <div className="shimmer" />}
      {src && <img className="zen-card-img" src={src} alt={alt} loading="lazy" onLoad={() => setLoaded(true)} onError={(e) => { e.target.style.display='none'; }} />}
    </div>
  );
};

export const PlaceCardFull = ({ place, selected, onToggle, onViewDetail }) => {
  const rawCat = place.category || 'popular';
  const cat = CAT_CLASS[rawCat] ? rawCat : 'popular';
  return (
    <article onClick={onViewDetail || onToggle}
      className={`zen-card ${selected ? 'selected' : ''}`}
      data-testid={`place-card-${place.name?.replace(/\s+/g, '-')}`}>
      <WikiImg wiki={place.wiki} alt={place.name} image={place.image} />
      <span className={`zen-badge ${CAT_CLASS[cat] || 'popular'}`}>{CAT_LABEL[cat] || 'POPULAR'}</span>
      {/* in words, not a tick (docs/producto 2.2 a) */}
      <div className="check-overlay" role="button" aria-pressed={selected} onClick={(e) => { if (onViewDetail) { e.stopPropagation(); onToggle(); } }}
        data-testid={`toggle-card-${place.name?.replace(/\s+/g, '-')}`}>
        {selected ? <><Check className="w-3.5 h-3.5" strokeWidth={3} /> In your trip</> : <><Plus className="w-3.5 h-3.5" strokeWidth={3} /> Add to my trip</>}
      </div>
      <div className="zen-card-body">
        <h3 className="zen-card-name">{place.name}</h3>
        {place.title && <p className="zen-card-title">{place.title}</p>}
        {place.description && <p className="zen-card-desc">{place.description}</p>}
        <div className="zen-card-footer">
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name + ", " + (place.city || "Japan"))}`}
            target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="zen-card-addr hover:underline transition-opacity hover:opacity-70"
            data-testid={`maps-link-${place.name?.replace(/\s+/g, '-')}`}>
            <MapPin className="w-3 h-3 shrink-0" style={{opacity:0.5}} />
            <span>{place.address || `${place.city}${place.neighborhood ? ` · ${place.neighborhood}` : ''}`}</span>
          </a>
          {(place.duration_text || place.duration) && (
            <div className="zen-card-dur">
              <Clock className="w-3 h-3" />
              {place.duration_text || `~${formatDuration(place.duration)}`}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
